import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import { protectedResources } from "../../../authConfig";
import { useToast } from "../../../contexts/ToastContext";

/**
 * Hook for invoice clarification functionality
 * Allows approvers to send invoices for clarification to IT managers
 */
export const useClarification = (invoiceId = null) => {
    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiScopes.scopes.write,
    });
    
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const sendToClarificationMutation = useMutation({
        mutationFn: async ({ invoiceId, approvalPackageId, itManagerId, message }) => {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/clarifications/send`,
                { invoiceId, approvalPackageId, itManagerId, message }
            );
            return response;
        },
        onSuccess: (data) => {
            showToast(
                `הבירור נשלח בהצלחה ל${data.data?.assignedTo || 'מנהל מערכות מידע'}`,
                "success"
            );
            queryClient.invalidateQueries(['clarifications', invoiceId]);
            queryClient.invalidateQueries(['invoices']);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 
                                error.message || 
                                "שגיאה בשליחת הבירור";
            showToast(errorMessage, "error");
        }
    });

    const respondToClarificationMutation = useMutation({
        mutationFn: async ({ clarificationId, responseText, approved }) => {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/clarifications/respond`,
                { clarificationId, responseText, approved }
            );
            return response;
        },
        onSuccess: () => {
            showToast("התגובה נשלחה בהצלחה", "success");
            queryClient.invalidateQueries(['clarifications']);
            queryClient.invalidateQueries(['clarification']); // refreshes useClarificationById on standalone page
            queryClient.invalidateQueries(['pendingClarifications']);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 
                                error.message || 
                                "שגיאה בשליחת התגובה";
            showToast(errorMessage, "error");
        }
    });

    const { data: clarifications = [], isLoading: isLoadingClarifications } = useQuery({
        queryKey: ['clarifications', invoiceId],
        queryFn: async () => {
            if (!invoiceId) return [];
            const response = await execute(
                "GET",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/clarifications/by-invoice/${invoiceId}`
            );
            return response || [];
        },
        enabled: !!invoiceId,
        staleTime: 30 * 1000
    });

    const { data: pendingClarifications = [], isLoading: isLoadingPending, refetch: refetchPending } = useQuery({
        queryKey: ['pendingClarifications'],
        queryFn: async () => {
            const response = await execute(
                "GET",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/clarifications/pending`
            );
            return response || [];
        },
        staleTime: 60 * 1000
    });

    return {
        sendToClarification: sendToClarificationMutation.mutate,
        isSendingClarification: sendToClarificationMutation.isPending,

        respondToClarification: respondToClarificationMutation.mutate,
        isRespondingToClarification: respondToClarificationMutation.isPending,

        clarifications,
        isLoadingClarifications,

        pendingClarifications,
        isLoadingPending,
        refetchPending,

        hasPendingClarification: clarifications.some(c => c.status === 'pending')
    };
};

export const useClarificationById = (clarificationId) => {
    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiScopes.scopes.write,
    });

    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const { data: clarification, isLoading, error } = useQuery({
        queryKey: ['clarification', clarificationId],
        queryFn: async () => {
            const response = await execute(
                "GET",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/clarifications/${clarificationId}`
            );
            return response;
        },
        enabled: !!clarificationId,
        staleTime: 30 * 1000,
        retry: (failureCount, err) => {
            if (err?.response?.status === 403 || err?.response?.status === 404) return false;
            return failureCount < 2;
        }
    });

    const respondToClarificationMutation = useMutation({
        mutationFn: async ({ clarificationId: cId, responseText, approved }) => {
            const response = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/clarifications/respond`,
                { clarificationId: cId, responseText, approved }
            );
            return response;
        },
        onSuccess: () => {
            showToast("התגובה נשלחה בהצלחה", "success");
            queryClient.invalidateQueries(['clarification', clarificationId]);
        },
        onError: (err) => {
            const message = err.response?.data?.message || err.message || "שגיאה בשליחת התגובה";
            showToast(message, "error");
        }
    });

    return {
        clarification,
        isLoading,
        error,
        respondToClarification: respondToClarificationMutation.mutate,
        isResponding: respondToClarificationMutation.isPending,
        respondSuccess: respondToClarificationMutation.isSuccess,
    };
};

export default useClarification;
